import config from '../../config';
import { getAuthHeaders } from './common';

const ultrasoundOrderApi = {
  /**
   * Create an imaging order for a client through the legacy ultrasound-order route.
   * The backend persists the order, generates the PDF, and returns a pre-signed S3 URL.
   *
   * @param {Object} params
   * @param {string} params.clientReferenceId
   * @param {string|null} params.pregnancyReferenceId - Required for obstetric studies
   * @param {string} params.orderingProviderReferenceId
   * @param {string[]} params.indications - Array of indication enum keys (e.g. "ANATOMY_SCAN")
   * @param {string} [params.icd10CodesRaw]
   * @param {string} [params.icd10Codes] - Backward-compatible alias mapped to icd10CodesRaw
   * @param {string} [params.notes]
   * @param {{ type: 'typed', name: string } | { type: 'drawn', dataUrl: string }} params.signature
   * @param {string} [params.generatedAt] - ISO timestamp
   * @returns {Promise<{ referenceId: string, pdfUrl: string, filename: string }>}
   */
  createUltrasoundOrder: async ({ clientReferenceId, pregnancyReferenceId, ...payload }) => {
    const url = `${config.api.url}/clients/${clientReferenceId}/ultrasound-orders`;
    const { icd10Codes, icd10CodesRaw, ...rest } = payload;
    const body = {
      pregnancyReferenceId,
      ...rest,
      ...((icd10CodesRaw ?? icd10Codes) !== undefined && {
        icd10CodesRaw: icd10CodesRaw ?? icd10Codes,
      }),
    };

    const res = await fetch(url, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(body),
    });

    if (!res.ok) {
      const errorText = await res.text();
      const error = new Error(errorText || `Imaging order failed: ${res.status}`);
      error.status = res.status;
      throw error;
    }

    const json = await res.json();

    const referenceId = json.referenceId ?? json.ReferenceId ?? null;
    const pdfUrl     = json.pdfUrl     ?? json.PdfUrl     ?? null;
    const filename   = json.fileName   ?? json.FileName   ??
      `imaging-order-${clientReferenceId}-${new Date().toISOString().slice(0, 10).replace(/-/g, '')}.pdf`;

    if (!pdfUrl) {
      const error = new Error('PDF_GENERATION_FAILED');
      error.referenceId = referenceId;
      throw error;
    }

    return { referenceId, pdfUrl, filename };
  },

  /**
   * List all imaging orders for a client (most recent first).
   *
   * @param {string} clientReferenceId
   * @returns {Promise<Array<{ referenceId: string, createdAt: string, indications: string[], filename: string }>>}
   */
  listUltrasoundOrders: async (clientReferenceId) => {
    const url = `${config.api.url}/clients/${clientReferenceId}/ultrasound-orders`;

    const res = await fetch(url, {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    if (!res.ok) {
      const errorText = await res.text();
      throw new Error(errorText || `Failed to list imaging orders: ${res.status}`);
    }

    return res.json();
  },

  /**
   * Get a fresh pre-signed S3 URL for an existing imaging order PDF.
   * Use this when the original pdfUrl from createUltrasoundOrder has expired.
   *
   * @param {string} clientReferenceId
   * @param {string} orderReferenceId
   * @returns {Promise<{ pdfUrl: string }>}
   */
  getUltrasoundOrderPdfUrl: async (clientReferenceId, orderReferenceId) => {
    const url = `${config.api.url}/clients/${clientReferenceId}/ultrasound-orders/${orderReferenceId}/pdf-url`;

    const res = await fetch(url, {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    if (!res.ok) {
      const errorText = await res.text();
      throw new Error(errorText || `Failed to get PDF URL: ${res.status}`);
    }

    const json = await res.json();
    return { pdfUrl: json.pdfUrl ?? json.PdfUrl ?? null };
  },

  /**
   * Delete an imaging order from a client's chart.
   *
   * @param {string} clientReferenceId
   * @param {string} orderReferenceId
   * @returns {Promise<void>}
   */
  deleteUltrasoundOrder: async (clientReferenceId, orderReferenceId) => {
    const url = `${config.api.url}/clients/${clientReferenceId}/ultrasound-orders/${orderReferenceId}`;

    const res = await fetch(url, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });

    if (!res.ok) {
      const errorText = await res.text();
      throw new Error(errorText || `Failed to delete ultrasound order: ${res.status}`);
    }
  },

  /**
   * Send an imaging order as a fax — handled server-side so the backend fetches
   * the PDF directly from S3, avoiding browser CORS restrictions.
   *
   * @param {Object} params
   * @param {string} params.clientReferenceId
   * @param {string} params.orderReferenceId
   * @param {string} params.toNumber - Destination fax number in E.164 format
   * @returns {Promise<void>}
   */
  sendUltrasoundOrderFax: async ({ clientReferenceId, orderReferenceId, toNumber }) => {
    const url = `${config.api.url}/clients/${clientReferenceId}/ultrasound-orders/${orderReferenceId}/fax`;

    const res = await fetch(url, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ destinationFaxNumber: toNumber }),
    });

    if (!res.ok) {
      const errorText = await res.text();
      throw new Error(errorText || `Failed to send fax: ${res.status}`);
    }
  },
};

export default ultrasoundOrderApi;
